// Copyright 2026, Imprivata, Inc.  All rights reserved.

import { useEffect, useRef, useState, type ReactNode } from 'react';
import styles from './EamcChatbotSideDock.module.css';

interface EamcChatbotSideDockProps {
  /** Whether the full chat panel is open. When false, renders the launcher FAB. */
  isOpen: boolean;
  /** Called when the user clicks the launcher FAB to re-open the panel. */
  onOpen: () => void;
  children: ReactNode;
}

/**
 * Positioning wrapper that fixes the chatbot to the bottom-right of the
 * viewport.
 *
 * Design contract:
 * - Fixed position, bottom-right corner, high z-index so it floats above
 *   portal content without disturbing the existing layout flow.
 * - No scrolling, no pointer-event capture on the backdrop.
 * - Open/close state is owned by the parent (EamcChatbotGate). The upstream
 *   EamcChatbot component is stateless with respect to visibility — it signals
 *   close via the `chat_panel_closed` telemetry event and the host handles it.
 * - When closed, renders a launcher FAB so the user can reopen the panel.
 * - Expansion detection: a MutationObserver watches the chatbot component's own
 *   root element (first child of the inner wrapper) for CSS class changes.
 *   When `EamcChatbot` internally adds its `__expanded` CSS-module class, the
 *   host switches to the `sideDockExpanded` positioning class and also
 *   overrides the chatbot root's inline `height` to `100%` so the element fills
 *   the repositioned container (top: 76 px → bottom: 0) instead of the full
 *   viewport height the chatbot's own CSS requests (LYRA-1722).
 */
export const EamcChatbotSideDock = ({ isOpen, onOpen, children }: EamcChatbotSideDockProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  // Ref to the inner wrapper div that hosts the EamcChatbot component.
  const innerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) {
      // Reset expansion state when the panel is closed so compact mode is
      // restored when the user reopens it.
      setIsExpanded(false);
      return () => {};
    }

    // Watch the EamcChatbot component's own root element (first child of our
    // wrapper) for class attribute changes. The chatbot adds a CSS-module class
    // matching the pattern `eamc-chatbot__expanded___*` to its root when the
    // user activates the expand control.
    const el = innerRef.current?.firstElementChild as HTMLElement | null;
    if (!el) {
      return () => {};
    }

    const hasExpandedClass = () => Array.from(el.classList).some(c => c.includes('__expanded'));

    // Override the chatbot's `height: 100dvh` with `height: 100%` so the host
    // container (repositioned to top: 76px → bottom: 0) controls the actual
    // bounds.  Without this, the chatbot overflows the container by 76 px
    // (the portal header height) and the input bar is pushed off-screen.
    const applyHeightOverride = (expanded: boolean) => {
      if (expanded) {
        el.style.height = '100%';
        el.style.minHeight = 'unset';
      } else {
        el.style.removeProperty('height');
        el.style.removeProperty('min-height');
      }
    };

    const sync = () => {
      const expanded = hasExpandedClass();
      applyHeightOverride(expanded);
      setIsExpanded(expanded);
    };

    const observer = new MutationObserver(sync);
    observer.observe(el, { attributes: true, attributeFilter: ['class'] });

    // Sync immediately in case the panel is already expanded on mount.
    sync();

    return () => observer.disconnect();
  }, [isOpen]);

  const dockClass = isOpen
    ? [styles.sideDock, isExpanded && styles.sideDockExpanded].filter(Boolean).join(' ')
    : styles.launcher;

  return (
    <div className={dockClass} data-testid="eamc-chatbot-side-dock">
      {isOpen ? (
        <div ref={innerRef}>{children}</div>
      ) : (
        <button
          type="button"
          className={styles.launcherButton}
          aria-label="Open EAMC assistant"
          onClick={onOpen}
        >
          💬
        </button>
      )}
    </div>
  );
};
